/*
 * component-cart-drawer.js - <cart-drawer> Custom Element.
 *
 * Responsibilities:
 *   - Open/close the native <dialog> on cart:open / cart:close / cart:item-added events.
 *   - Esc / backdrop / X / View-full-bag close the dialog.
 *   - Delegate qty-stepper change events to cartStore.change(line.key, qty).
 *   - Delegate remove-button clicks to cartStore.change(line.key, 0).
 *   - Delegate promo form submit to cartStore.applyDiscount(code) with a client-side
 *     bundle_active guard (Phase 7 always false; Phase 8 wires the real flag).
 *   - Delegate Tag remove click to cartStore.removeDiscount().
 *
 * Re-binding on SRA swap:
 *   - When the dialog is CLOSED at mutation time: cart-store swaps the whole
 *     <cart-drawer> wrapper; this element's connectedCallback re-runs and
 *     listeners attach to the fresh DOM.
 *   - When the dialog is OPEN at mutation time: cart-store preserves the live
 *     <dialog> and only swaps its children (to avoid re-triggering the
 *     `@starting-style` slide-in animation on every qty/remove click).
 *     connectedCallback does NOT re-fire in this path - listeners survive
 *     because they're attached to the still-existing dialog and host.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._onCartOpen = this._onCartOpen.bind(this);
    this._onCartClose = this._onCartClose.bind(this);
    this._onItemAdded = this._onItemAdded.bind(this);
    this._onDialogClose = this._onDialogClose.bind(this);
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onError = this._onError.bind(this);
    this._onCartUpdated = this._onCartUpdated.bind(this);
  }

  connectedCallback() {
    this.dialog = this.querySelector('dialog.cart-drawer__dialog');
    if (!this.dialog) return;

    document.addEventListener('cart:open', this._onCartOpen);
    document.addEventListener('cart:close', this._onCartClose);
    document.addEventListener('cart:item-added', this._onItemAdded);
    document.addEventListener('cart:error', this._onError);
    document.addEventListener('cart:updated', this._onCartUpdated);

    this.dialog.addEventListener('close', this._onDialogClose);
    this.dialog.addEventListener('click', this._onClick);
    this.addEventListener('qty-stepper:change', this._onChange);
    this.addEventListener('submit', this._onSubmit);

    // Hydrate immediately in case a code was already applied before mount
    // (page reload with an existing cart.discount_codes entry).
    this._hydrateAppliedChipsFromServer();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:open', this._onCartOpen);
    document.removeEventListener('cart:close', this._onCartClose);
    document.removeEventListener('cart:item-added', this._onItemAdded);
    document.removeEventListener('cart:error', this._onError);
    document.removeEventListener('cart:updated', this._onCartUpdated);
    if (this.dialog) {
      this.dialog.removeEventListener('close', this._onDialogClose);
      this.dialog.removeEventListener('click', this._onClick);
    }
    this.removeEventListener('qty-stepper:change', this._onChange);
    this.removeEventListener('submit', this._onSubmit);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.dispatchEvent(new CustomEvent('cart:opened', { bubbles: true }));
  }

  close() {
    if (!this.dialog || !this.dialog.open) return;
    this.dialog.close();
  }

  _onCartOpen() { this.open(); }
  _onCartClose() { this.close(); }

  _onItemAdded() {
    // Respect the merchant setting cart_drawer_open_on_add: when set to false,
    // the data attribute is "false" - we read it via the host element.
    const flag = this.dataset.openOnAdd;
    if (flag === 'false') return;
    this.open();
  }

  _onDialogClose() {
    document.documentElement.style.overflow = '';
    this.dispatchEvent(new CustomEvent('cart:closed', { bubbles: true }));
    const trigger = document.querySelector('[data-cart-link]');
    if (trigger) trigger.focus();
  }

  _onClick(e) {
    // Backdrop click: <dialog> targets the element itself when the backdrop is clicked.
    if (e.target === this.dialog) {
      this.close();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      this.close();
      return;
    }
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      const line = remove.closest('[data-line-key]');
      if (line && window.cartStore) {
        window.cartStore.change(line.dataset.lineKey, 0).catch(() => { /* error broadcast via cart:error */ });
      }
      return;
    }
    const removeDiscount = e.target.closest('[data-cart-discount-remove]');
    if (removeDiscount && window.cartStore) {
      window.cartStore.removeDiscount().catch(() => {});
      return;
    }
    // Phase 8: bump-line CTA inside <cart-bundle-upsell>. The button carries
    // data-action="bump-line" and data-line-key. Bump quantity by 1 on the
    // first eligible line; cart-store SRA-swaps the drawer and the upsell CE
    // re-renders on the cart:updated event.
    const bump = e.target.closest('[data-action="bump-line"]');
    if (bump && window.cartStore) {
      const key = bump.dataset.lineKey;
      if (!key) return;
      // Read the current quantity off the corresponding cart-line's qty input.
      // Fall back to 1 if the element is not in the DOM yet.
      let currentQty = 1;
      const lineEl = this.querySelector('[data-line-key="' + (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key) + '"]');
      if (lineEl) {
        const input = lineEl.querySelector('qty-stepper input[type="number"]');
        if (input && input.value) {
          const parsed = parseInt(input.value, 10);
          if (!isNaN(parsed) && parsed > 0) currentQty = parsed;
        }
      }
      window.cartStore.change(key, currentQty + 1).catch(() => {});
      return;
    }
  }

  _onChange(e) {
    const stepper = e.target.closest('qty-stepper');
    if (!stepper) return;
    const line = stepper.closest('[data-line-key]');
    if (!line || !window.cartStore) return;
    const detail = e.detail || {};
    const qty = Math.max(1, Number(detail.value) || 1);
    window.cartStore.change(line.dataset.lineKey, qty).catch(() => {});
  }

  _onSubmit(e) {
    const form = e.target.closest('[data-promo-form]');
    if (!form) return;
    e.preventDefault();
    const messageRegion = form.parentNode && form.parentNode.querySelector('[data-message-region]');
    const input = form.querySelector('input[name="discount"]');
    if (messageRegion) messageRegion.textContent = '';
    if (input) input.setAttribute('aria-invalid', 'false');

    const code = (input && input.value || '').trim();
    if (!code) return;

    const host = form.closest('cart-discount');
    const bundleActive = host && host.dataset.bundleActive === 'true';
    if (bundleActive) {
      this._renderPromoError(form, 'bundle_active');
      this._announceAlert(this._messageForCode('bundle_active'));
      return;
    }

    if (window.cartStore) {
      const applyBtn = form.querySelector('.cart-discount__apply');
      if (applyBtn) { applyBtn.disabled = true; applyBtn.classList.add('is-loading'); }
      window.cartStore.applyDiscount(code)
        .catch(() => { /* error broadcast via cart:error */ })
        .finally(() => {
          if (applyBtn) { applyBtn.disabled = false; applyBtn.classList.remove('is-loading'); }
        });
    }
  }

  _onError(e) {
    const detail = e && e.detail;
    if (!detail || detail.op !== 'discount') return;
    // Find the active promo form (drawer or cart page) and surface the message.
    const forms = document.querySelectorAll('[data-promo-form]');
    forms.forEach((form) => this._renderPromoError(form, detail.code));
  }

  _onCartUpdated(e) {
    const cart = e && e.detail && e.detail.cart;
    if (!cart) return;
    this._hydrateAppliedChips(cart.discount_codes || [], cart);
  }

  _hydrateAppliedChipsFromServer() {
    // Only called at connect time. Best-effort fetch; failures are silently
    // ignored - the next cart:updated will hydrate anyway.
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => r.ok ? r.json() : null)
      .then((cart) => { if (cart) this._hydrateAppliedChips(cart.discount_codes || [], cart); })
      .catch(() => { /* ignore */ });
  }

  // Populate every .cart-discount__applied region on the page with the
  // first applicable code from cart.discount_codes (Shopify non-Plus allows
  // only one code at checkout). When no code is applicable, the region
  // is hidden. Also clears any stale error state on a successful apply.
  //
  // When the applied code produces no cart-level discount (cart.total_discount
  // === 0), it's a shipping-type discount that only allocates at checkout.
  // Mirror the checkout UI locally: strikethrough the estimated shipping
  // amount + append FREE, and drop shipping from the estimated total.
  _hydrateAppliedChips(discountCodes, cart) {
    const applicable = (discountCodes || []).find((dc) => dc && dc.applicable);
    const regions = document.querySelectorAll('[data-applied-region]');
    regions.forEach((region) => {
      const label = region.querySelector('[data-applied-label]');
      const removeBtn = region.querySelector('[data-cart-discount-remove]');
      if (applicable && label) {
        label.textContent = applicable.code;
        region.hidden = false;
        if (removeBtn) removeBtn.setAttribute('aria-label', 'Remove discount ' + applicable.code);
      } else {
        region.hidden = true;
        if (label) label.textContent = '';
      }
    });
    if (applicable) {
      // Clear any lingering error UI (e.g. user rejected a code, then applied
      // a valid one).
      document.querySelectorAll('cart-discount').forEach((host) => {
        const region = host.querySelector('[data-message-region]');
        const input = host.querySelector('input[name="discount"]');
        if (region) region.textContent = '';
        if (input) input.setAttribute('aria-invalid', 'false');
      });
    }
    // Shipping-row + total override: only reliable when we know cart's
    // total_discount. If caller didn't pass the cart (server-hydration path
    // before cart:updated), skip - the next cart:updated will pass it.
    if (cart && typeof cart.total_discount === 'number') {
      const freeShippingByCode = !!applicable && cart.total_discount === 0;
      this._applyFreeShippingByCode(freeShippingByCode, cart);
    }
  }

  _applyFreeShippingByCode(active, cart) {
    document.querySelectorAll('[data-cart-shipping-row]').forEach((row) => {
      const shipEl = row.querySelector('[data-cart-shipping]');
      if (!shipEl) return;
      // Row hidden by unlocked-threshold logic - Shopify's SRA render already
      // dropped it, nothing to override.
      if (row.hidden) return;
      if (active) {
        if (!shipEl.dataset.originalHtml) shipEl.dataset.originalHtml = shipEl.innerHTML;
        shipEl.innerHTML = '<s>' + shipEl.dataset.originalHtml + '</s> <span class="cart-totals__shipping-free">' + (window.LunchedCartMessages && window.LunchedCartMessages.free) + '</span>';
        row.setAttribute('data-shipping-free-by-code', 'true');
      } else if (shipEl.dataset.originalHtml) {
        shipEl.innerHTML = shipEl.dataset.originalHtml;
        delete shipEl.dataset.originalHtml;
        row.removeAttribute('data-shipping-free-by-code');
      }
    });
    document.querySelectorAll('[data-cart-total]').forEach((totalEl) => {
      const shippingEl = totalEl.closest('.cart-totals')?.querySelector('[data-cart-shipping]');
      let shipping = 0;
      if (shippingEl && !active) {
        const raw = shippingEl.dataset.originalHtml || shippingEl.textContent || '';
        const digits = raw.replace(/[^\d.]/g, '');
        shipping = digits ? Math.round(parseFloat(digits) * 100) : 0;
      }
      const cents = cart.total_price + shipping;
      totalEl.textContent = this._formatMoney(cents);
    });
    // Free-shipping progress bar: server-rendered state reads cart.total_price
    // against the subtotal threshold and can't see shipping-type codes (which
    // never appear in cart.discount_applications at cart-time). When a code
    // has made shipping free, flip the bar to its unlocked visual state.
    document.querySelectorAll('.cart-fsb').forEach((fsb) => {
      const labelEl = fsb.querySelector('.cart-fsb__label');
      const fillEl = fsb.querySelector('.cart-fsb__fill');
      if (!labelEl || !fillEl) return;
      if (active) {
        if (fsb.dataset.freeByCodeOriginalLabel === undefined) {
          fsb.dataset.freeByCodeOriginalLabel = labelEl.innerHTML;
          fsb.dataset.freeByCodeOriginalFillWidth = fillEl.style.width || '';
          fsb.dataset.freeByCodeWasUnlocked = fsb.classList.contains('cart-fsb--unlocked') ? '1' : '0';
        }
        fsb.classList.add('cart-fsb--unlocked');
        const unlockedText = (window.LunchedCartMessages && window.LunchedCartMessages.free_shipping_unlocked) || 'Free shipping unlocked';
        // innerHTML rather than textContent so HTML entities in the locale
        // string (e.g. &#39; from | t | json) render as their glyphs.
        labelEl.innerHTML = unlockedText;
        fillEl.style.width = '100%';
      } else if (fsb.dataset.freeByCodeOriginalLabel !== undefined) {
        labelEl.innerHTML = fsb.dataset.freeByCodeOriginalLabel;
        fillEl.style.width = fsb.dataset.freeByCodeOriginalFillWidth;
        if (fsb.dataset.freeByCodeWasUnlocked !== '1') {
          fsb.classList.remove('cart-fsb--unlocked');
        }
        delete fsb.dataset.freeByCodeOriginalLabel;
        delete fsb.dataset.freeByCodeOriginalFillWidth;
        delete fsb.dataset.freeByCodeWasUnlocked;
      }
    });
  }

  _formatMoney(cents) {
    // Peso formatter matching Shopify's `| money` filter output (₱20.00).
    // Not transient: this runs AFTER the SRA swap on cart:updated, so the
    // formatted string persists until the next cart mutation.
    try {
      return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format((cents || 0) / 100);
    } catch (_) {
      return '₱' + ((cents || 0) / 100).toFixed(2);
    }
  }

  _renderPromoError(form, code) {
    const message = this._messageForCode(code);
    const region = form.parentNode && form.parentNode.querySelector('[data-message-region]');
    const input = form.querySelector('input[name="discount"]');
    if (region) region.textContent = message;
    if (input) input.setAttribute('aria-invalid', 'true');
    // Force the disclosure open so the message is visible. The SRA-rendered
    // HTML replaces the <details> without preserving its open state (the
    // fresh element defaults to closed), so an error rendered here would
    // otherwise be hidden behind a folded summary. Liquid can't detect the
    // rejected code (cart.discount_applications only lists actually-allocated
    // discounts), so we handle this client-side.
    const details = form.closest('details');
    if (details) details.open = true;
  }

  _messageForCode(code) {
    const messages = window.LunchedCartMessages || {};
    const raw = messages[code] || messages.generic || 'That code could not be applied.';
    return this._decodeHtmlEntities(raw);
  }

  _decodeHtmlEntities(s) {
    // Shopify Liquid's `| t` filter HTML-escapes apostrophes/quotes/etc before
    // `| json` wraps the string for JS. textContent rendering would then show
    // literal `&#39;` instead of `'`. textarea-innerHTML decode is the safe
    // way to undo this (plaintext context, no tag parsing).
    if (typeof s !== 'string') return s;
    if (s.indexOf('&') === -1) return s;
    const txt = document.createElement('textarea');
    txt.innerHTML = s;
    return txt.value;
  }

  _announceAlert(text) {
    const region = document.getElementById('cart-live-alert');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = 'Error: ' + text; }, 50);
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
